/**
 * Built-in `record-list` widget — a small BI table.
 *
 * TWO SOURCES, ONE RENDERER:
 *   • `file` — a CSV / Excel upload. Rows are spreadsheet rows, keys are its
 *     column headers. (The original behaviour; boards saved before the cell
 *     source existed carry no `source` field and still land here.)
 *   • `cell` — another cell's plugin output, read off the board's cell bus.
 *     Rows are runs, keys are dot paths into each run's `result`.
 *
 * Both extract into the same `Grid` (`keys` + `rows`), so column picking,
 * custom headers, units, decimals and the table/list layouts are written once
 * and behave identically whichever source is feeding them.
 *
 * Why a cell source addresses cell → PLUGIN: a viewer cell installs every
 * plugin its snapshot names, so one checkpoint can carry slope, volume and
 * deform at once. Leaving the plugin unset lists them all, one row each, which
 * is the "what did this checkpoint compute?" view.
 *
 * SheetJS (xlsx) is lazy-loaded from a CDN and handles .csv and Excel alike.
 * On base-portable SITE builds it is served from the site's own bundle instead
 * (zero public-CDN egress) — see ../../vendor.
 */
import type {
  DashboardWidget,
  DashboardWidgetInstance,
  DashboardCtx,
  CellFeed,
  CellPlugin,
  CellRun,
} from '../../Widget';
import { vendorLibUrl } from '../../vendor';

type Cell = string | number | boolean | null;

/** One displayed column: which key, and how it is titled and formatted. */
export interface ColSpec {
  /** File source: the spreadsheet column name. Cell source: a dot path into the
   *  run's `result`, or one of the pseudo-keys `@plugin` / `@time` / `@run`. */
  key: string;
  /** Custom header; defaults to the column name / the path's last segment. */
  header?: string;
  /** Appended after the value (`m³`, `mm`, `°`). */
  unit?: string;
  /** Fixed decimals for numbers; omitted = trimmed to at most 3. */
  decimals?: number;
}

export interface RecordListConfig {
  /** Where the rows come from. Absent = `file` (back-compat). */
  source?: 'file' | 'cell';

  // --- file source ---
  filename?: string;
  sheetName?: string;
  columns: string[];
  rows: Cell[][];

  // --- cell source ---
  /** Cell id of the engine cell to read. */
  sourceCellId?: string;
  /** Plugin inside that cell. Unset = every plugin. */
  pluginId?: string;
  /** `plugin` = one row per plugin (its latest run). `run` = one row per run. */
  rowMode?: 'plugin' | 'run';

  // --- display ---
  /** Columns to show, in order. Empty/unset = every key found. */
  cols?: ColSpec[];
  /** `table` = a grid. `list` = key/value lines per row (better in a narrow cell). */
  layout?: 'table' | 'list';
  /** Heading override. */
  title?: string;
}

const C = {
  bg: '#0c0f13',
  head: '#0f1318',
  th: '#14181d',
  line: '#1c222a',
  rule: '#161b22',
  edge: '#2a313a',
  text: '#d8dee9',
  dim: '#9aa4ad',
  faint: '#7d8a96',
  accent: '#2c5cff',
  danger: '#f0a4a4',
};

/** Columns offered before the operator picks any. */
const AUTO_COL_CAP = 12;
/** Ceiling on key discovery, so a big payload stays usable in the form. */
const FLATTEN_CAP = 500;
const FLATTEN_DEPTH = 6;
/** Pseudo-keys a cell source always offers, whatever the plugin returned. */
const META_KEYS = ['@plugin', '@time', '@run'] as const;

const XLSX_JS_URL = vendorLibUrl(
  'xlsx-0.18.5.full.min.js',
  'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js',
);
let xlsxLoader: Promise<any> | null = null;
function loadXLSX(): Promise<any> {
  if ((window as any).XLSX) return Promise.resolve((window as any).XLSX);
  if (!xlsxLoader) {
    xlsxLoader = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = XLSX_JS_URL;
      s.async = true;
      s.onload = () => resolve((window as any).XLSX);
      s.onerror = () => reject(new Error('SheetJS (xlsx) load failed'));
      document.head.appendChild(s);
    });
  }
  return xlsxLoader;
}

function emptyConfig(): RecordListConfig {
  return { source: 'file', columns: [], rows: [] };
}

// ---------------------------------------------------------------------------
// The common shape both sources produce
// ---------------------------------------------------------------------------

interface Grid {
  /** Every key present, in offer order. */
  keys: string[];
  rows: Array<Record<string, unknown>>;
  /** Shown instead of the grid when there is nothing to show. */
  empty?: string;
  /** Shown in the header's right slot. */
  note?: string;
}

type Scalar = string | number | boolean | null;

function isScalar(v: unknown): v is Scalar {
  return v === null || ['string', 'number', 'boolean'].includes(typeof v);
}

/** Walk a run result into `path → value` pairs. Scalars only: a column holds one value. */
export function flattenResult(
  value: unknown,
  prefix = '',
  out: Array<{ path: string; value: Scalar }> = [],
  depth = 0,
): Array<{ path: string; value: Scalar }> {
  if (out.length >= FLATTEN_CAP) return out;
  if (isScalar(value)) {
    if (prefix) out.push({ path: prefix, value });
    return out;
  }
  if (depth >= FLATTEN_DEPTH) return out;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length && out.length < FLATTEN_CAP; i++) {
      flattenResult(value[i], prefix ? `${prefix}.${i}` : String(i), out, depth + 1);
    }
    return out;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (out.length >= FLATTEN_CAP) break;
      flattenResult(v, prefix ? `${prefix}.${k}` : k, out, depth + 1);
    }
  }
  return out;
}

export function defaultHeader(key: string): string {
  if (key === '@plugin') return 'Plugin';
  if (key === '@time') return 'Time';
  if (key === '@run') return 'Run';
  return key.split('.').pop() || key;
}

export function formatValue(v: unknown, spec?: ColSpec): string {
  if (v === undefined || v === null || v === '') return v === '' ? '' : '—';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return '—';
    const s = spec?.decimals != null ? v.toFixed(spec.decimals) : String(Number(v.toFixed(3)));
    return spec?.unit ? `${s} ${spec.unit}` : s;
  }
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'string') return spec?.unit ? `${v} ${spec.unit}` : v;
  return Array.isArray(v) ? `[${v.length}]` : '{…}';
}

function lastRun(p: CellPlugin | undefined): CellRun | undefined {
  return p?.runs?.length ? p.runs[p.runs.length - 1] : undefined;
}

/** Spreadsheet rows → the common grid. */
function gridFromFile(cfg: RecordListConfig): Grid {
  const keys = cfg.columns ?? [];
  if (!keys.length && !cfg.rows?.length) {
    return { keys: [], rows: [], empty: 'No data — open Configure to upload a CSV or Excel file.' };
  }
  const rows = (cfg.rows ?? []).map((r) => {
    const o: Record<string, unknown> = {};
    keys.forEach((k, i) => {
      o[k] = r?.[i] ?? null;
    });
    return o;
  });
  return {
    keys,
    rows,
    note: `${rows.length} rows · ${keys.length} cols`,
  };
}

/** One cell's plugin output → the common grid. */
function gridFromCell(cfg: RecordListConfig, feed: CellFeed | undefined): Grid {
  if (!feed) return { keys: [], rows: [], empty: 'waiting for that cell' };

  const plugins = cfg.pluginId
    ? feed.plugins.filter((p) => p.id === cfg.pluginId)
    : feed.plugins;
  if (!plugins.length) {
    return {
      keys: [],
      rows: [],
      empty: cfg.pluginId
        ? `${cfg.pluginId} is not running in ${cfg.sourceCellId}.`
        : `${cfg.sourceCellId} is running no plugins.`,
    };
  }

  // `plugin` mode answers "what did this checkpoint compute?" — one line per
  // plugin. `run` mode answers "how did this number move?" — one line per run.
  const picked: Array<{ p: CellPlugin; r: CellRun }> = [];
  for (const p of plugins) {
    if (cfg.rowMode === 'run') {
      for (const r of p.runs) picked.push({ p, r });
    } else {
      const r = lastRun(p);
      if (r) picked.push({ p, r });
    }
  }
  if (cfg.rowMode === 'run') picked.sort((a, b) => a.r.time.localeCompare(b.r.time));

  if (!picked.length) {
    return { keys: [], rows: [], empty: 'no run yet' };
  }

  const keys: string[] = [...META_KEYS];
  const rows = picked.map(({ p, r }) => {
    const row: Record<string, unknown> = {
      '@plugin': p.label || p.id,
      '@time': r.time ? new Date(r.time).toLocaleString() : '',
      '@run': r.id,
    };
    if (r.error) {
      row['@error'] = r.error;
      if (!keys.includes('@error')) keys.push('@error');
      return row;
    }
    for (const { path, value } of flattenResult(r.result)) {
      row[path] = value;
      if (!keys.includes(path)) keys.push(path);
    }
    return row;
  });

  return { keys, rows, note: `${rows.length} ${rows.length === 1 ? 'run' : 'rows'}` };
}

function el(tag: string, css: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  n.style.cssText = css;
  if (text != null) n.textContent = text;
  return n;
}

class RecordListInstance implements DashboardWidgetInstance {
  private host: HTMLElement;
  private config: RecordListConfig;
  private ctx: DashboardCtx;
  private feeds: ReadonlyMap<string, CellFeed> = new Map();
  private unsub: (() => void) | null = null;
  /** Set while the config drawer is open, so a fresh run refreshes its key list. */
  private repaintConfig: (() => void) | null = null;

  constructor(host: HTMLElement, config: RecordListConfig, ctx: DashboardCtx) {
    this.host = host;
    this.config = config;
    this.ctx = ctx;
    if (ctx.subscribe) {
      // Fires immediately — a reader mounted after the producer already ran
      // shows its numbers straight away rather than on the next change.
      this.unsub = ctx.subscribe((feeds) => {
        this.feeds = feeds;
        if (this.config.source === 'cell') this.render();
        this.repaintConfig?.();
      });
    }
    this.render();
  }

  private placeholder(text: string, danger = false): void {
    this.host.innerHTML = '';
    this.host.appendChild(
      el(
        'div',
        'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;'
          + `color:${danger ? C.danger : C.faint};font-size:12px;text-align:center;padding:10px;line-height:1.5`,
        text,
      ),
    );
  }

  /** The three different silences a cell source can hit — they are not
   *  interchangeable, and none of them is a zero. */
  private cellEmptyReason(): string | null {
    const cfg = this.config;
    if (!this.ctx.subscribe) return 'This dashboard build has no cell bus.';
    if (!cfg.sourceCellId) return 'Open Configure and pick an engine cell to read.';
    if (this.feeds.get(cfg.sourceCellId)) return null;
    const summary = this.ctx.cells?.().find((c) => c.id === cfg.sourceCellId);
    if (!summary) return `${cfg.sourceCellId} is not on this tab.`;
    return summary.type === 'viewer-snapshot'
      ? `${cfg.sourceCellId} has no scene loaded yet.`
      : `${cfg.sourceCellId} is a ${summary.type} cell — it produces no plugin results.`;
  }

  private buildGrid(): Grid {
    const cfg = this.config;
    if (cfg.source !== 'cell') return gridFromFile(cfg);
    const reason = this.cellEmptyReason();
    if (reason) return { keys: [], rows: [], empty: reason };
    return gridFromCell(cfg, this.feeds.get(cfg.sourceCellId!));
  }

  /** Columns to render: the operator's picks, or a sensible default. */
  private columnsFor(grid: Grid): ColSpec[] {
    const picked = this.config.cols?.filter((c) => c.key);
    if (picked?.length) return picked;
    // Unconfigured: show what is there. `@run` is dropped from the default —
    // it is an internal id, useful to pick but noise by default.
    return grid.keys.filter((k) => k !== '@run').slice(0, AUTO_COL_CAP).map((key) => ({ key }));
  }

  private render(): void {
    const grid = this.buildGrid();
    if (grid.empty) {
      this.placeholder(grid.empty);
      return;
    }

    this.host.innerHTML = '';
    const wrap = el(
      'div',
      `position:absolute;inset:0;display:flex;flex-direction:column;background:${C.bg};color:${C.text};font-size:12px`,
    );

    const cfg = this.config;
    const heading = cfg.title
      || (cfg.source === 'cell'
        ? this.feeds.get(cfg.sourceCellId ?? '')?.title || cfg.sourceCellId
        : cfg.sheetName
          ? `${cfg.filename} · ${cfg.sheetName}`
          : cfg.filename);
    if (heading || grid.note) {
      const hdr = el(
        'div',
        `flex:0 0 auto;padding:4px 8px;font-size:11px;color:${C.dim};border-bottom:1px solid ${C.line};`
          + `background:${C.head};display:flex;justify-content:space-between;gap:8px`,
      );
      const left = el('span', 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap');
      left.textContent = heading ?? '';
      hdr.appendChild(left);
      if (grid.note) hdr.appendChild(el('span', 'flex:0 0 auto', grid.note));
      wrap.appendChild(hdr);
    }

    const cols = this.columnsFor(grid);
    const scroll = el('div', 'flex:1 1 auto;overflow:auto');
    scroll.appendChild(
      cfg.layout === 'list' ? this.renderList(grid, cols) : this.renderTable(grid, cols),
    );
    wrap.appendChild(scroll);
    this.host.appendChild(wrap);
  }

  private renderTable(grid: Grid, cols: ColSpec[]): HTMLElement {
    const tbl = document.createElement('table');
    tbl.style.cssText =
      'border-collapse:collapse;font-size:11px;font-family:ui-monospace,Menlo,monospace;white-space:nowrap;min-width:100%';

    const thead = document.createElement('thead');
    const trh = document.createElement('tr');
    for (const c of cols) {
      const th = document.createElement('th');
      th.textContent = c.header || defaultHeader(c.key);
      th.title = c.key;
      th.style.cssText =
        `position:sticky;top:0;background:${C.th};color:${C.dim};text-align:left;padding:4px 8px;`
        + `border-bottom:1px solid ${C.edge};font-weight:600`;
      trh.appendChild(th);
    }
    thead.appendChild(trh);
    tbl.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (let i = 0; i < grid.rows.length; i++) {
      const tr = document.createElement('tr');
      if (i % 2 === 1) tr.style.background = '#10141a';
      for (const c of cols) {
        const td = document.createElement('td');
        const v = grid.rows[i]?.[c.key];
        td.textContent = formatValue(v, c);
        td.style.cssText = `padding:3px 8px;border-bottom:1px solid ${C.rule};color:${
          v === undefined ? C.faint : C.text
        }`;
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    tbl.appendChild(tbody);
    return tbl;
  }

  /** Key/value lines — what a narrow KPI cell wants. One block per row. */
  private renderList(grid: Grid, cols: ColSpec[]): HTMLElement {
    const box = el('div', 'display:flex;flex-direction:column');
    grid.rows.forEach((row, i) => {
      if (grid.rows.length > 1) {
        const sub = el(
          'div',
          `padding:${i ? '8px' : '5px'} 8px 2px;font-size:10px;color:${C.faint};`
            + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
        );
        sub.textContent = String(row['@plugin'] ?? `row ${i + 1}`);
        box.appendChild(sub);
      }
      for (const c of cols) {
        // The plugin name is the block's own heading — repeating it as a line
        // inside the block is the same fact twice.
        if (grid.rows.length > 1 && c.key === '@plugin') continue;
        const line = el(
          'div',
          `display:flex;justify-content:space-between;gap:10px;padding:3px 8px;border-bottom:1px solid ${C.rule}`,
        );
        const k = el('span', `color:${C.dim};overflow:hidden;text-overflow:ellipsis;white-space:nowrap`);
        k.textContent = c.header || defaultHeader(c.key);
        k.title = c.key;
        line.appendChild(k);
        line.appendChild(
          el(
            'span',
            `flex:0 0 auto;font-family:ui-monospace,Menlo,monospace;color:${C.text}`,
            formatValue(row[c.key], c),
          ),
        );
        box.appendChild(line);
      }
    });
    return box;
  }

  // -------------------------------------------------------------------------
  // Config form
  // -------------------------------------------------------------------------

  renderConfigUI(host: HTMLElement, onChange: (cfg: unknown) => void): void {
    // Draft config: text inputs must not re-mount the cell on every keystroke,
    // so edits land here and Apply commits them.
    const draft: RecordListConfig = {
      ...this.config,
      cols: (this.config.cols ?? []).map((c) => ({ ...c })),
    };

    const input = (css = ''): HTMLInputElement => {
      const i = document.createElement('input');
      i.type = 'text';
      i.style.cssText =
        `padding:3px 5px;font-size:11px;background:${C.head};color:${C.text};border:1px solid ${C.edge};border-radius:3px;` + css;
      return i;
    };
    const button = (text: string, primary = false): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = text;
      b.style.cssText = primary
        ? `padding:4px 10px;font-size:11px;background:${C.accent};color:#fff;border:0;border-radius:3px;cursor:pointer`
        : `padding:3px 8px;font-size:11px;background:${C.head};color:${C.text};border:1px solid ${C.edge};border-radius:3px;cursor:pointer`;
      return b;
    };
    const select = (): HTMLSelectElement => {
      const s = document.createElement('select');
      s.style.cssText = `padding:4px;font-size:11px;background:${C.head};color:${C.text};border:1px solid ${C.edge};border-radius:3px`;
      return s;
    };
    const label = (t: string): HTMLElement => el('label', `font-size:11px;color:${C.dim}`, t);

    let status = '';
    let statusDanger = false;

    const paint = (): void => {
      host.innerHTML = '';
      const form = el('div', 'display:flex;flex-direction:column;gap:8px;font-size:12px;min-width:260px');

      // --- source switch ---
      form.appendChild(label('Data from'));
      const seg = el('div', 'display:flex;gap:6px');
      const mkSeg = (val: 'file' | 'cell', text: string): HTMLElement => {
        const b = button(text, (draft.source ?? 'file') === val);
        b.addEventListener('click', () => {
          draft.source = val;
          paint();
        });
        return b;
      };
      seg.appendChild(mkSeg('file', 'Upload file'));
      seg.appendChild(mkSeg('cell', 'Engine cell'));
      form.appendChild(seg);

      const isCell = draft.source === 'cell';

      // --- source-specific pickers ---
      if (!isCell) {
        form.appendChild(this.fileSection(draft, paint, button, (t, d) => {
          status = t;
          statusDanger = !!d;
        }));
        if (status) {
          form.appendChild(
            el('div', `font-size:11px;color:${statusDanger ? C.danger : C.faint}`, status),
          );
        }
      } else if (!this.ctx.subscribe) {
        form.appendChild(el('div', `color:${C.danger};line-height:1.4`, 'This dashboard build has no cell bus.'));
      } else {
        form.appendChild(label('Engine cell'));
        const cellSel = select();
        const cells = this.ctx.cells?.() ?? [];
        const none = document.createElement('option');
        none.value = '';
        none.textContent = cells.length ? '— pick a cell —' : '— no other cells on this tab —';
        cellSel.appendChild(none);
        for (const c of cells) {
          const o = document.createElement('option');
          o.value = c.id;
          o.textContent = c.publishes
            ? `${c.id}${c.title ? ` · ${c.title}` : ''}`
            : `${c.id} · ${c.type} (no output)`;
          if (c.id === draft.sourceCellId) o.selected = true;
          cellSel.appendChild(o);
        }
        cellSel.addEventListener('change', () => {
          draft.sourceCellId = cellSel.value || undefined;
          // A different cell means a different plugin set and different keys.
          draft.pluginId = undefined;
          draft.cols = [];
          paint();
        });
        form.appendChild(cellSel);

        const feed = draft.sourceCellId ? this.feeds.get(draft.sourceCellId) : undefined;
        if (draft.sourceCellId && !feed) {
          form.appendChild(
            el('div', `font-size:11px;color:${C.faint};line-height:1.4`,
              'That cell has no scene loaded yet — load one and this form will catch up.'),
          );
        }
        if (feed) {
          form.appendChild(label('Plugin'));
          const pSel = select();
          const all = document.createElement('option');
          all.value = '';
          all.textContent = `— all plugins (${feed.plugins.length}) —`;
          pSel.appendChild(all);
          for (const p of feed.plugins) {
            const o = document.createElement('option');
            o.value = p.id;
            o.textContent = `${p.label || p.id}${p.runs.length ? '' : ' (no run yet)'}`;
            if (p.id === draft.pluginId) o.selected = true;
            pSel.appendChild(o);
          }
          pSel.addEventListener('change', () => {
            draft.pluginId = pSel.value || undefined;
            draft.cols = [];
            paint();
          });
          form.appendChild(pSel);

          form.appendChild(label('One row per'));
          const rSel = select();
          for (const [v, t] of [
            ['plugin', 'Plugin — its latest run'],
            ['run', 'Run — the whole history'],
          ] as const) {
            const o = document.createElement('option');
            o.value = v;
            o.textContent = t;
            if ((draft.rowMode ?? 'plugin') === v) o.selected = true;
            rSel.appendChild(o);
          }
          rSel.addEventListener('change', () => {
            draft.rowMode = rSel.value as 'plugin' | 'run';
            paint();
          });
          form.appendChild(rSel);
        }
      }

      // --- column picker (identical for both sources) ---
      const grid = this.previewGrid(draft);
      if (grid.keys.length) {
        form.appendChild(label('Columns to show'));
        const btns = el('div', 'display:flex;gap:6px');
        const allBtn = button('All');
        allBtn.addEventListener('click', () => {
          draft.cols = grid.keys.map((key) => ({ key }));
          paint();
        });
        const noneBtn = button('None');
        noneBtn.addEventListener('click', () => {
          draft.cols = [];
          paint();
        });
        btns.appendChild(allBtn);
        btns.appendChild(noneBtn);
        form.appendChild(btns);

        const list = el(
          'div',
          `display:flex;flex-direction:column;gap:2px;max-height:300px;overflow:auto;`
            + `border:1px solid ${C.edge};border-radius:3px;padding:4px;background:${C.bg}`,
        );
        for (const key of grid.keys) {
          const spec = draft.cols?.find((c) => c.key === key);
          const row = el('div', 'display:flex;flex-direction:column;gap:2px;padding:2px 0');

          const top = el('label', 'display:flex;align-items:center;gap:6px;cursor:pointer');
          const cb = document.createElement('input');
          cb.type = 'checkbox';
          cb.checked = !!spec;
          cb.addEventListener('change', () => {
            if (cb.checked) (draft.cols ??= []).push({ key });
            else draft.cols = (draft.cols ?? []).filter((c) => c.key !== key);
            paint();
          });
          const nameEl = el(
            'span',
            `flex:1 1 auto;font-family:ui-monospace,Menlo,monospace;font-size:10px;color:${C.dim};`
              + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
            key,
          );
          nameEl.title = key;
          // The first row's value, so keys are picked against real data.
          const sample = grid.rows[0]?.[key];
          const valEl = el(
            'span',
            `flex:0 0 auto;font-size:10px;color:${C.faint};font-family:ui-monospace,Menlo,monospace;max-width:38%;`
              + 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
            sample === undefined ? '' : formatValue(sample),
          );
          top.appendChild(cb);
          top.appendChild(nameEl);
          top.appendChild(valEl);
          row.appendChild(top);

          if (spec) {
            const edit = el('div', 'display:flex;gap:4px;padding-left:20px');
            const hdr = input('flex:2 1 auto;min-width:0');
            hdr.placeholder = defaultHeader(key);
            hdr.value = spec.header ?? '';
            hdr.addEventListener('input', () => {
              spec.header = hdr.value || undefined;
            });
            const unit = input('flex:1 1 40px;min-width:0');
            unit.placeholder = 'unit';
            unit.value = spec.unit ?? '';
            unit.addEventListener('input', () => {
              spec.unit = unit.value || undefined;
            });
            const dp = input('flex:0 0 34px;min-width:0');
            dp.placeholder = 'dp';
            dp.value = spec.decimals != null ? String(spec.decimals) : '';
            dp.addEventListener('input', () => {
              const n = Number(dp.value);
              spec.decimals =
                dp.value.trim() === '' || !Number.isFinite(n) ? undefined : Math.max(0, Math.min(6, Math.round(n)));
            });
            edit.appendChild(hdr);
            edit.appendChild(unit);
            edit.appendChild(dp);
            row.appendChild(edit);
          }
          list.appendChild(row);
        }
        form.appendChild(list);
        form.appendChild(
          el(
            'div',
            `font-size:10px;color:${C.faint};line-height:1.4`,
            `Ticked: ${draft.cols?.length ?? 0}. None ticked = show the first ${AUTO_COL_CAP} columns.`,
          ),
        );
      }

      // --- layout + heading ---
      form.appendChild(label('Layout'));
      const lSel = select();
      for (const [v, t] of [
        ['table', 'Table — a column per key'],
        ['list', 'List — a line per key'],
      ] as const) {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = t;
        if ((draft.layout ?? 'table') === v) o.selected = true;
        lSel.appendChild(o);
      }
      lSel.addEventListener('change', () => {
        draft.layout = lSel.value as 'table' | 'list';
      });
      form.appendChild(lSel);

      form.appendChild(label('Heading (optional)'));
      const titleIn = input();
      titleIn.placeholder = isCell ? 'defaults to the scene name' : 'defaults to the filename';
      titleIn.value = draft.title ?? '';
      titleIn.addEventListener('input', () => {
        draft.title = titleIn.value || undefined;
      });
      form.appendChild(titleIn);

      const apply = button('Apply', true);
      apply.style.alignSelf = 'flex-start';
      apply.addEventListener('click', () => onChange({ ...draft }));
      form.appendChild(apply);

      host.appendChild(form);
    };

    // A run finishing while the drawer is open should offer its new keys.
    this.repaintConfig = paint;
    paint();
  }

  /** The grid the form picks columns against — the draft's source, not the
   *  live config's, so the picker follows the switch before Apply. */
  private previewGrid(draft: RecordListConfig): Grid {
    if (draft.source !== 'cell') return gridFromFile(draft);
    if (!draft.sourceCellId) return { keys: [], rows: [] };
    return gridFromCell(draft, this.feeds.get(draft.sourceCellId));
  }

  private fileSection(
    draft: RecordListConfig,
    paint: () => void,
    button: (t: string, p?: boolean) => HTMLButtonElement,
    setStatus: (t: string, danger?: boolean) => void,
  ): HTMLElement {
    const box = el('div', 'display:flex;flex-direction:column;gap:6px');
    box.appendChild(
      el(
        'div',
        `color:${C.dim};line-height:1.4;font-size:11px`,
        'CSV or Excel (.csv / .xlsx / .xls). First row is the header; multi-sheet workbooks use the first sheet.',
      ),
    );
    if (draft.filename) {
      box.appendChild(
        el(
          'div',
          `font-size:11px;color:${C.faint}`,
          `Loaded: ${draft.filename}${draft.sheetName ? ` · ${draft.sheetName}` : ''} (${draft.rows?.length ?? 0} rows)`,
        ),
      );
    }

    const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    const fileBtn = button('Upload CSV / Excel…', true);
    const fileIn = document.createElement('input');
    fileIn.type = 'file';
    fileIn.accept =
      '.csv,.tsv,.xlsx,.xls,.xlsm,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    fileIn.style.display = 'none';
    fileBtn.addEventListener('click', () => fileIn.click());
    fileIn.addEventListener('change', async () => {
      const f = fileIn.files?.[0];
      if (!f) return;
      setStatus(`Parsing ${f.name}…`);
      paint();
      try {
        const XLSX = await loadXLSX();
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) throw new Error('no sheets found');
        const matrix: Cell[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], {
          header: 1,
          raw: true,
          defval: null,
        });
        if (!matrix.length) {
          setStatus('File is empty', true);
          paint();
          return;
        }
        draft.filename = f.name;
        draft.sheetName = sheetName;
        draft.columns = matrix[0]!.map((c, i) => (c == null || c === '' ? `col${i + 1}` : String(c)));
        draft.rows = matrix.slice(1);
        // A new sheet means new columns; stale picks would silently show "—".
        draft.cols = [];
        setStatus(`Loaded ${f.name} · ${sheetName} (${draft.rows.length} rows). Pick columns, then Apply.`);
        paint();
      } catch (e) {
        setStatus(`Load failed: ${(e as Error).message}`, true);
        paint();
      } finally {
        fileIn.value = '';
      }
    });

    const clearBtn = button('Clear');
    clearBtn.addEventListener('click', () => {
      draft.filename = undefined;
      draft.sheetName = undefined;
      draft.columns = [];
      draft.rows = [];
      draft.cols = [];
      setStatus('');
      paint();
    });

    row.appendChild(fileBtn);
    row.appendChild(fileIn);
    row.appendChild(clearBtn);
    box.appendChild(row);
    return box;
  }

  // No getState/applyState: a file's parsed table and a cell reference both
  // live in `config` and round-trip through the layout JSON.

  dispose(): void {
    this.repaintConfig = null;
    this.unsub?.();
    this.unsub = null;
    this.host.innerHTML = '';
  }
}

export const recordListWidget: DashboardWidget = {
  id: 'record-list',
  label: 'List / table',
  description:
    'A table from an uploaded CSV / Excel file, or from another cell’s plugin results. Pick the columns, name the headers.',
  mount(host, config, ctx) {
    const base = emptyConfig();
    const cfg =
      config && typeof config === 'object'
        ? ({ ...base, ...(config as Partial<RecordListConfig>) } as RecordListConfig)
        : base;
    // Defend against malformed shapes (and boards saved before these existed).
    if (!Array.isArray(cfg.columns)) cfg.columns = [];
    if (!Array.isArray(cfg.rows)) cfg.rows = [];
    if (!Array.isArray(cfg.cols)) cfg.cols = [];
    if (cfg.source !== 'cell') cfg.source = 'file';
    return new RecordListInstance(host, cfg, ctx);
  },
};
